import { createContext, useContext, useState, useCallback, useRef } from 'react';

export interface BubbleStep {
  message: string;
  duration: number; // seconds to show before advancing
}

interface BubbleState {
  steps: BubbleStep[];
  currentStep: number;
  isVisible: boolean;
}

interface AImryBubbleContextValue {
  bubble: BubbleState;
  showBubble: (steps: BubbleStep[]) => void;
  advanceStep: () => void;
  hideBubble: () => void;
}

const AImryBubbleContext = createContext<AImryBubbleContextValue | null>(null);

export function AImryBubbleProvider({ children }: { children: React.ReactNode }) {
  const [bubble, setBubble] = useState<BubbleState>({
    steps: [],
    currentStep: 0,
    isVisible: false,
  });
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const hideBubble = useCallback(() => {
    clearTimers();
    setBubble({ steps: [], currentStep: 0, isVisible: false });
  }, [clearTimers]);

  const advanceStep = useCallback(() => {
    setBubble((prev) => {
      const next = prev.currentStep + 1;
      if (next >= prev.steps.length) {
        // Last step — schedule hide after its duration
        return prev;
      }
      return { ...prev, currentStep: next };
    });
  }, []);

  const showBubble = useCallback(
    (steps: BubbleStep[]) => {
      clearTimers();
      setBubble({ steps, currentStep: 0, isVisible: true });

      // Auto-advance through steps based on durations
      let elapsed = 0;
      steps.forEach((step, i) => {
        elapsed += step.duration * 1000;
        if (i < steps.length - 1) {
          // Advance to next step
          const timer = setTimeout(() => {
            setBubble((prev) => ({ ...prev, currentStep: i + 1 }));
          }, elapsed);
          timersRef.current.push(timer);
        } else {
          // Hide after last step
          const timer = setTimeout(() => {
            setBubble({ steps: [], currentStep: 0, isVisible: false });
          }, elapsed);
          timersRef.current.push(timer);
        }
      });
    },
    [clearTimers],
  );

  return (
    <AImryBubbleContext.Provider value={{ bubble, showBubble, advanceStep, hideBubble }}>
      {children}
    </AImryBubbleContext.Provider>
  );
}

export function useAImryBubble() {
  const ctx = useContext(AImryBubbleContext);
  if (!ctx) throw new Error('useAImryBubble must be used inside AImryBubbleProvider');
  return ctx;
}
